import * as React from "react";
import * as ReactDOM from "react-dom";
import * as AC from "adaptivecards";
import { ThemeProvider } from "@fluentui/react";
import { Fluent2WebDarkTheme } from "@fluentui/fluent2-theme";
import { registerChartImageExporter } from "./chartExportRegistry.js";
import {
    DonutChart,
    VerticalBarChart,
    GroupedVerticalBarChart,
    HorizontalBarChartWithAxis,
    MultiStackedBarChart,
    LineChart,
    GaugeChart,
    GaugeValueFormat
} from "@fluentui/react-charting";

export const CHART_TYPE_NAMES = [
    "Chart.Donut",
    "Chart.Pie",
    "Chart.Gauge",
    "Chart.VerticalBar",
    "Chart.VerticalBar.Grouped",
    "Chart.HorizontalBar",
    "Chart.HorizontalBar.Stacked",
    "Chart.Line"
];

export const NAMED_COLORS = {
    good: "#13a10e",
    warning: "#eaa300",
    attention: "#c50f1f",
    neutral: "#8a8886",
    categoricalRed: "#d13438",
    categoricalPurple: "#5c2e91",
    categoricalLavender: "#9a7fea",
    categoricalBlue: "#0078d4",
    categoricalLightBlue: "#00bcf2",
    categoricalTeal: "#038387",
    categoricalGreen: "#498205",
    categoricalLime: "#8cbd18",
    categoricalMarigold: "#ffaa44",
    sequential1: "#cfe4fa",
    sequential2: "#a6d0f7",
    sequential3: "#7dbaf5",
    sequential4: "#4da3f5",
    sequential5: "#1a86ef",
    sequential6: "#106ebe",
    sequential7: "#005a9e",
    sequential8: "#004578",
    sequentialred1: "#f9d9d6",
    sequentialred2: "#f3b3ad",
    sequentialred3: "#e88b83",
    sequentialred4: "#d13438",
    sequentialgreen1: "#d5e8c8",
    sequentialgreen2: "#b4d6a0",
    sequentialgreen3: "#8cc56e",
    sequentialgreen4: "#498205",
    sequentialyellow1: "#ffe8b8",
    sequentialyellow2: "#ffd078",
    sequentialyellow3: "#ffb900",
    sequentialyellow4: "#c19c00",
    divergingBlue: "#004e8c",
    divergingLightBlue: "#0096e2",
    divergingCyan: "#50e6ff",
    divergingTeal: "#0cafa9",
    divergingYellow: "#ffd23b",
    divergingPeach: "#ff8c8c",
    divergingLightRed: "#e81123",
    divergingRed: "#a4262c",
    divergingMaroon: "#750b1c",
    divergingGray: "#737373"
};

export const COLOR_SETS = {
    categorical: [
        "categoricalRed",
        "categoricalPurple",
        "categoricalLavender",
        "categoricalBlue",
        "categoricalLightBlue",
        "categoricalTeal",
        "categoricalGreen",
        "categoricalLime",
        "categoricalMarigold"
    ],
    sequential: [
        "sequential1",
        "sequential2",
        "sequential3",
        "sequential4",
        "sequential5",
        "sequential6",
        "sequential7",
        "sequential8"
    ],
    sequentialred: ["sequentialred1", "sequentialred2", "sequentialred3", "sequentialred4"],
    sequentialgreen: ["sequentialgreen1", "sequentialgreen2", "sequentialgreen3", "sequentialgreen4"],
    sequentialyellow: ["sequentialyellow1", "sequentialyellow2", "sequentialyellow3", "sequentialyellow4"],
    diverging: [
        "divergingBlue",
        "divergingLightBlue",
        "divergingCyan",
        "divergingTeal",
        "divergingYellow",
        "divergingPeach",
        "divergingLightRed",
        "divergingRed",
        "divergingMaroon",
        "divergingGray"
    ]
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CHART_HEIGHT = 220;

export function stripArgb(color) {
    if (typeof color !== "string") return color;
    if (/^#[0-9a-fA-F]{8}$/.test(color)) {
        return `#${color.slice(3)}`;
    }
    return color;
}

export function resolveChartColor(colorName, colorSet, index, hostConfig) {
    const key = typeof colorName === "string" ? colorName : "";
    if (key && NAMED_COLORS[key]) {
        return NAMED_COLORS[key];
    }
    const semantic = hostConfig?.containerStyles?.default?.foregroundColors?.[key]?.default;
    if (semantic) {
        return stripArgb(semantic);
    }
    const setName = typeof colorSet === "string" ? colorSet.toLowerCase() : "categorical";
    const palette = COLOR_SETS[setName] || COLOR_SETS.categorical;
    const mapped = NAMED_COLORS[palette[index % palette.length]];
    return mapped || NAMED_COLORS.categoricalBlue;
}

export function chartTitle(source) {
    if (!source || source.showTitle === false) return undefined;
    return source.title;
}

export function isIsoDateString(value) {
    return typeof value === "string" && ISO_DATE.test(value);
}

export function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function asArray(value) {
    return Array.isArray(value) ? value : [];
}

function mapSlicePoints(source, hostConfig) {
    return asArray(source.data).map((point, index) => ({
        legend: point.legend || point.x || `Item ${index + 1}`,
        data: toNumber(point.value ?? point.y),
        color: resolveChartColor(point.color, source.colorSet, index, hostConfig)
    }));
}

function mapXyBars(source, hostConfig) {
    return asArray(source.data).map((point, index) => ({
        x: point.x == null ? `Item ${index + 1}` : point.x,
        y: toNumber(point.y ?? point.value),
        legend: point.legend || String(point.x ?? `Item ${index + 1}`),
        color: resolveChartColor(point.color, source.colorSet, index, hostConfig)
    }));
}

function mapLineX(values) {
    const allDates = values.length > 0 && values.every((point) => isIsoDateString(point.x));
    return (point, index) => {
        const y = toNumber(point.y);
        if (allDates) {
            return { x: new Date(`${point.x}T00:00:00`), y, xAxisCalloutData: point.x };
        }
        if (typeof point.x === "number") {
            return { x: point.x, y };
        }
        return { x: index, y, xAxisCalloutData: String(point.x ?? index) };
    };
}

export function mapChartModel(typeName, source = {}, hostConfig) {
    const title = chartTitle(source);
    const commonAxes = {
        chartTitle: title,
        xAxisTitle: source.xAxisTitle,
        yAxisTitle: source.yAxisTitle,
        hideLegend: source.showLegend === false
    };

    switch (typeName) {
        case "Chart.Donut":
            return {
                kind: "donut",
                props: {
                    ...commonAxes,
                    data: { chartTitle: title, chartData: mapSlicePoints(source, hostConfig) },
                    innerRadius: 55,
                    hideLabels: false
                }
            };
        case "Chart.Pie":
            return {
                kind: "donut",
                props: {
                    ...commonAxes,
                    data: { chartTitle: title, chartData: mapSlicePoints(source, hostConfig) },
                    innerRadius: 0,
                    hideLabels: false
                }
            };
        case "Chart.VerticalBar":
            return {
                kind: "verticalBar",
                props: {
                    ...commonAxes,
                    data: mapXyBars(source, hostConfig),
                    hideLabels: source.showBarValues === false
                }
            };
        case "Chart.HorizontalBar":
            return {
                kind: "horizontalBar",
                props: {
                    ...commonAxes,
                    data: mapXyBars(source, hostConfig).map((point) => ({
                        x: point.y,
                        y: String(point.x),
                        legend: point.legend,
                        color: point.color
                    }))
                }
            };
        case "Chart.VerticalBar.Grouped": {
            const seriesList = asArray(source.data);
            const categories = [];
            const seen = new Set();
            seriesList.forEach((series) => {
                asArray(series.values).forEach((point) => {
                    const x = String(point.x);
                    if (!seen.has(x)) {
                        seen.add(x);
                        categories.push(x);
                    }
                });
            });
            return {
                kind: "groupedVerticalBar",
                props: {
                    ...commonAxes,
                    data: categories.map((name) => ({
                        name,
                        series: seriesList.map((series, seriesIndex) => {
                            const point = asArray(series.values).find((item) => String(item.x) === name);
                            return {
                                key: series.legend || `series-${seriesIndex}`,
                                legend: series.legend || `Series ${seriesIndex + 1}`,
                                data: toNumber(point?.y),
                                color: resolveChartColor(series.color, source.colorSet, seriesIndex, hostConfig)
                            };
                        })
                    }))
                }
            };
        }
        case "Chart.HorizontalBar.Stacked":
            return {
                kind: "stackedHorizontalBar",
                props: {
                    ...commonAxes,
                    hideRatio: asArray(source.data).map(() => true),
                    data: asArray(source.data).map((row) => ({
                        chartTitle: row.title,
                        chartData: asArray(row.data).map((segment, index) => ({
                            legend: segment.legend || `Segment ${index + 1}`,
                            data: toNumber(segment.value),
                            color: resolveChartColor(segment.color, source.colorSet, index, hostConfig)
                        }))
                    }))
                }
            };
        case "Chart.Line": {
            const seriesList = asArray(source.data);
            const allValues = seriesList.flatMap((series) => asArray(series.values));
            const toPoint = mapLineX(allValues);
            return {
                kind: "line",
                props: {
                    ...commonAxes,
                    data: {
                        chartTitle: title,
                        lineChartData: seriesList.map((series, seriesIndex) => ({
                            legend: series.legend || `Series ${seriesIndex + 1}`,
                            color: resolveChartColor(series.color, source.colorSet, seriesIndex, hostConfig),
                            data: asArray(series.values).map(toPoint)
                        }))
                    }
                }
            };
        }
        case "Chart.Gauge": {
            const segments = asArray(source.segments).map((segment, index) => ({
                legend: segment.legend || `Segment ${index + 1}`,
                size: toNumber(segment.size ?? segment.value),
                color: resolveChartColor(segment.color, source.colorSet, index, hostConfig)
            }));
            const valueFormat = String(source.valueFormat || "percentage").toLowerCase() === "fraction"
                ? GaugeValueFormat.Fraction
                : GaugeValueFormat.Percentage;
            const minValue = toNumber(source.minValue ?? source.min, 0);
            const maxSource = source.maxValue ?? source.max;
            return {
                kind: "gauge",
                props: {
                    chartTitle: title,
                    chartValue: toNumber(source.value),
                    minValue,
                    maxValue: maxSource == null ? undefined : toNumber(maxSource),
                    segments,
                    chartValueFormat: valueFormat,
                    hideLegend: source.showLegend === false,
                    hideMinMax: source.showMinMax === false,
                    sublabel: source.subLabel
                }
            };
        }
        default:
            return {
                kind: "unsupported",
                props: { typeName, title: source.title }
            };
    }
}

const KIND_COMPONENTS = {
    donut: DonutChart,
    verticalBar: VerticalBarChart,
    groupedVerticalBar: GroupedVerticalBarChart,
    horizontalBar: HorizontalBarChartWithAxis,
    stackedHorizontalBar: MultiStackedBarChart,
    line: LineChart,
    gauge: GaugeChart
};

export function isDarkHost(hostConfig) {
    const background = hostConfig?.containerStyles?.default?.backgroundColor;
    return Boolean(background && background !== "#ffffff");
}

export { registerChartImageExporter, getChartImageExporter } from "./chartExportRegistry.js";

export class AdaptiveCardChartElement extends AC.CardElement {
    constructor() {
        super();
        this.chartSource = {};
    }

    internalParse(source, context) {
        super.internalParse(source, context);
        this.chartSource = source || {};
    }

    getJsonTypeName() {
        return this.chartSource?.type || "Chart";
    }

    renderReact(width = 320, onChartReady) {
        const model = mapChartModel(this.getJsonTypeName(), this.chartSource, this.hostConfig);
        const ChartComponent = KIND_COMPONENTS[model.kind];
        const chart = ChartComponent
            ? <ChartComponent
                {...model.props}
                width={width}
                height={CHART_HEIGHT}
                componentRef={onChartReady}
            />
            : <div className="ac-chart-unsupported">Unsupported chart: {model.props.typeName}</div>;
        const theme = isDarkHost(this.hostConfig) ? Fluent2WebDarkTheme : undefined;
        return <ThemeProvider theme={theme}>{chart}</ThemeProvider>;
    }

    internalRender() {
        const element = document.createElement("div");
        element.className = "ac-chart";
        element.style.width = "100%";
        element.style.minHeight = `${CHART_HEIGHT}px`;
        const bindExporter = (api) => {
            if (typeof api?.toImage === "function") {
                registerChartImageExporter(element, (opts) => api.toImage(opts));
            }
        };
        let lastWidth = -1;
        const mount = (width) => {
            const nextWidth = width > 0 ? width : 320;
            if (nextWidth === lastWidth) return;
            lastWidth = nextWidth;
            ReactDOM.render(this.renderReact(nextWidth, bindExporter), element);
        };
        mount(element.clientWidth);
        const schedule = () => {
            const width = element.clientWidth;
            if (width > 0) mount(width);
        };
        requestAnimationFrame(schedule);
        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(schedule);
            observer.observe(element);
        }
        return element;
    }
}

function createChartClass(typeName) {
    return class extends AdaptiveCardChartElement {
        getJsonTypeName() {
            return typeName;
        }
    };
}

export const ChartDonut = createChartClass("Chart.Donut");
export const ChartPie = createChartClass("Chart.Pie");
export const ChartGauge = createChartClass("Chart.Gauge");
export const ChartVerticalBar = createChartClass("Chart.VerticalBar");
export const ChartVerticalBarGrouped = createChartClass("Chart.VerticalBar.Grouped");
export const ChartHorizontalBar = createChartClass("Chart.HorizontalBar");
export const ChartHorizontalBarStacked = createChartClass("Chart.HorizontalBar.Stacked");
export const ChartLine = createChartClass("Chart.Line");

export const CHART_ELEMENTS = {
    "Chart.Donut": ChartDonut,
    "Chart.Pie": ChartPie,
    "Chart.Gauge": ChartGauge,
    "Chart.VerticalBar": ChartVerticalBar,
    "Chart.VerticalBar.Grouped": ChartVerticalBarGrouped,
    "Chart.HorizontalBar": ChartHorizontalBar,
    "Chart.HorizontalBar.Stacked": ChartHorizontalBarStacked,
    "Chart.Line": ChartLine
};

export function registerAdaptiveCardCharts(registry = AC.GlobalRegistry.elements) {
    Object.keys(CHART_ELEMENTS).forEach((typeName) => {
        registry.register(typeName, CHART_ELEMENTS[typeName]);
    });
    return registry;
}

export function enableAdaptiveCardChartHostCapabilities(hostConfig) {
    if (!hostConfig?.hostCapabilities?.addCapability) return hostConfig;
    hostConfig.hostCapabilities.addCapability("adaptiveCards", "*");
    hostConfig.hostCapabilities.addCapability("charts", "*");
    return hostConfig;
}
